/**
 * Design System - Spacing Tokens
 *
 * Consistent spacing scale extracted from Figma design
 * Based on a 4px base unit system
 */

export const spacing = {
  // Base spacing units (4px increments)
  0: '0px',
  0.5: '2px', // 0.5 × 4 = 2px
  1: '4px', // 1 × 4 = 4px
  1.5: '6px', // 1.5 × 4 = 6px
  2: '8px', // 2 × 4 = 8px
  2.5: '10px', // 2.5 × 4 = 10px
  3: '12px', // 3 × 4 = 12px
  4: '16px', // 4 × 4 = 16px
  5: '20px', // 5 × 4 = 20px
  6: '24px', // 6 × 4 = 24px
  7: '28px', // 7 × 4 = 28px
  8: '32px', // 8 × 4 = 32px
  10: '40px', // 10 × 4 = 40px
  12: '48px', // 12 × 4 = 48px
  14: '56px', // 14 × 4 = 56px
  16: '64px', // 16 × 4 = 64px
  20: '80px', // 20 × 4 = 80px
  24: '96px', // 24 × 4 = 96px
  28: '112px', // 28 × 4 = 112px
  32: '128px', // 32 × 4 = 128px
} as const;

// Component-specific spacing
export const componentSpacing = {
  // Padding
  padding: {
    xs: spacing[1], // 4px
    sm: spacing[2], // 8px
    md: spacing[4], // 16px
    lg: spacing[6], // 24px
    xl: spacing[8], // 32px
    '2xl': spacing[12], // 48px
  },

  // Margins
  margin: {
    xs: spacing[1], // 4px
    sm: spacing[2], // 8px
    md: spacing[4], // 16px
    lg: spacing[6], // 24px
    xl: spacing[8], // 32px
    '2xl': spacing[12], // 48px
  },

  // Gaps (for flex/grid)
  gap: {
    xs: spacing[1], // 4px
    sm: spacing[2], // 8px
    md: spacing[4], // 16px
    lg: spacing[6], // 24px
    xl: spacing[8], // 32px
    '2xl': spacing[12], // 48px
  },

  // Section spacing
  section: {
    sm: spacing[12], // 48px
    md: spacing[16], // 64px
    lg: spacing[20], // 80px
    xl: spacing[24], // 96px
  },

  // Container padding
  container: {
    mobile: spacing[4], // 16px
    tablet: spacing[6], // 24px
    desktop: spacing[20], // 80px
  },
} as const;

// Layout spacing
export const layoutSpacing = {
  // Common layout values from design
  sidebarWidth: '106px',
  contentMaxWidth: '1400px',
  cardGap: '24px',
  sectionGap: '64px',
} as const;

export type SpacingToken = typeof spacing;
export type ComponentSpacingToken = typeof componentSpacing;

// Helper function to get spacing value
export const getSpacing = (key: keyof typeof spacing): string => {
  return spacing[key];
};
